import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";

import { createUrl, updateUrl } from "./api";
import type { Url, UrlCreate } from "./types";

interface Props {
  open: boolean;
  source: string;
  sourceId: number;
  link: Url | null;
  onClose: () => void;
  onSaved: () => void;
}

interface FormState {
  name: string;
  url: string;
}

const EMPTY: FormState = { name: "", url: "" };

function isValidUrl(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  if (v.startsWith("/")) return true;
  try {
    const u = new URL(v);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

export default function UrlDrawer(props: Props) {
  const { open, source, sourceId, link, onClose, onSaved } = props;
  const { enqueueSnackbar } = useSnackbar();

  const isNew = link === null;
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (link) {
      setForm({ name: link.name ?? "", url: link.url ?? "" });
    } else {
      setForm(EMPTY);
    }
  }, [open, link]);

  const update = <K extends keyof FormState>(key: K, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async () => {
    const name = form.name.trim();
    const url = form.url.trim();
    if (!name) {
      enqueueSnackbar("Name is required", { variant: "warning" });
      return;
    }
    if (!url) {
      enqueueSnackbar("URL is required", { variant: "warning" });
      return;
    }
    if (!isValidUrl(url)) {
      enqueueSnackbar(
        "URL must start with http:// or https:// or be a site-relative path (/...)",
        { variant: "warning" }
      );
      return;
    }

    const payload: UrlCreate = { name, url };

    setSaving(true);
    try {
      if (isNew) {
        await createUrl(source, sourceId, payload);
        enqueueSnackbar("Link added", { variant: "success" });
      } else {
        await updateUrl(source, sourceId, link!.id, payload);
        enqueueSnackbar("Link saved", { variant: "success" });
      }
      onSaved();
      onClose();
    } catch {
      // Global toast handles error
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: "100vw", sm: 420 }, p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {isNew ? "Add Link" : `Edit Link #${link?.id}`}
          </Typography>
          <IconButton onClick={onClose} aria-label="close drawer">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider sx={{ my: 2 }} />

        <Stack spacing={2}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            required
            fullWidth
            autoFocus
            inputProps={{ maxLength: 255 }}
          />
          <TextField
            label="URL"
            value={form.url}
            onChange={(e) => update("url", e.target.value)}
            required
            fullWidth
            placeholder="https://help.example.com/page or /help/page"
            inputProps={{ maxLength: 1024 }}
          />

          <Divider />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={submit}
              disabled={saving}
            >
              {isNew ? "Add" : "Save"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}
